import React, { useState, useEffect } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
  Button,
  Card,
  Space,
  Typography,
  Spin,
  Alert,
  Empty,
  Modal,
  message,
  Row,
  Col,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import KnowledgeBaseForm from "./KnowledgeBaseForm";
import API_BASE_URL from "../../config/api";
import { ELEVENLABS_CONFIG } from "../../config/elevenlabs-api";
import CustomPagination from "../paginations/CustomPagination";

const { Title, Text } = Typography;

const KnowledgeBaseList = () => {
  const [knowledgeBase, setKnowledgeBase] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    fetchKnowledgeBase();
  }, []);

  const fetchKnowledgeBase = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${ELEVENLABS_CONFIG.BASE_URL}/knowledge-base`,
        {
          headers: {
            "xi-api-key": ELEVENLABS_CONFIG.API_KEY,
          },
        }
      );
      setKnowledgeBase(response.data.documents);
    } catch (err) {
      console.error("Error fetching knowledge base:", err);
      setError("Failed to fetch knowledge base entries");
    } finally {
      setLoading(false);
    }
  };

  const handleAddEntry = () => {
    setShowForm(true);
  };

  const showDeleteConfirm = (id) => {
    const entry = knowledgeBase.find((item) => item.id === id);
    setSelectedEntry(entry);
    setDeleteModalVisible(true);
  };

  const handleDeleteModalOk = () => {
    if (selectedEntry) {
      handleDeleteEntry(selectedEntry.id);
      setDeleteModalVisible(false);
      setSelectedEntry(null);
    }
  };

  const handleDeleteModalCancel = () => {
    setDeleteModalVisible(false);
    setSelectedEntry(null);
  };

  const handleDeleteEntry = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${ELEVENLABS_CONFIG.BASE_URL}/knowledge-base/${id}`, {
        headers: {
          "xi-api-key": ELEVENLABS_CONFIG.API_KEY,
        },
      });
      message.success("Knowledge base entry deleted successfully");
      fetchKnowledgeBase(); // Refresh the list
    } catch (err) {
      console.error("Error deleting entry:", err);
      message.error("Failed to delete entry");
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
  };

  const handleFormSubmit = () => {
    setShowForm(false);
    fetchKnowledgeBase(); // Refresh the list
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "name",
      key: "name",
      render: (text) => <Typography.Text strong>{text}</Typography.Text>,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) => (
        <Typography.Text type="secondary">{type}</Typography.Text>
      ),
    },
    {
      title: "Size",
      dataIndex: ["metadata", "size_bytes"],
      key: "size",
      render: (size) => (
        <Typography.Text>
          {size ? `${(size / 1024).toFixed(2)} KB` : "0 KB"}
        </Typography.Text>
      ),
    },
    {
      title: "Created At",
      dataIndex: ["metadata", "created_at_unix_secs"],
      key: "createdAt",
      render: (date) => dayjs(date).format("DD-MM-YYYY"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            onClick={() => showDeleteConfirm(record.id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", padding: "50px" }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="p-4">
      
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 24 }}
        >
          <Col>
            <Title level={3} style={{ marginBottom: 8 }}>
              Knowledge Base
            </Title>
            <Text type="secondary">
              Manage your knowledge base documents and resources
            </Text>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddEntry}
            >
              Add Entry
            </Button>
          </Col>
        </Row>

        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "50px",
            }}
          >
            <Spin size="large" />
          </div>
        ) : (
          <>
            {error && (
              <Alert
                message="Error"
                description={error}
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {knowledgeBase.length === 0 ? (
              <Empty
                description="No knowledge base entries found"
                style={{ marginTop: 50 }}
              >
                <Button type="primary" onClick={handleAddEntry}>
                  Create First Entry
                </Button>
              </Empty>
            ) : (
              <>
                {/* <Table
                className="hidden-scroll"
                scroll={{ y: 600 }}
                  columns={columns}
                  dataSource={knowledgeBase}
                  rowKey="id"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                  }}
                /> */}
                <div className="rounded-2 table-responsive">
                <table className="table align-middle mb-0 shadow-sm" style={{ tableLayout: "auto" }}>
                  <thead className="table-light">
                    <tr>
                      <th>Title</th>
                      <th>Type</th>
                      <th>Size</th>
                      <th>Created At</th>
                      <th style={{ width: "100px" }} className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {knowledgeBase
                      .slice((pagination.current - 1) * pagination.pageSize, pagination.current * pagination.pageSize)
                      .map((entry) => (
                        <tr key={entry.id}>
                          <td><Typography.Text strong>{entry.name}</Typography.Text></td>
                          <td><Typography.Text type="secondary">{entry.type}</Typography.Text></td>
                          <td><Typography.Text>{entry.metadata?.size_bytes ? `${(entry.metadata.size_bytes / 1024).toFixed(2)} KB` : "0 KB"}</Typography.Text></td>
                          <td>{entry.metadata?.created_at_unix_secs ? dayjs(entry.metadata.created_at_unix_secs).format("DD-MM-YYYY") : ""}</td>
                          <td className="text-center">
                            <Space size="middle">
                              <Button
                                type="text"
                                icon={<DeleteOutlined style={{ color: "red" }}/>}
                                onClick={() => showDeleteConfirm(entry.id)}
                              >
                               
                              </Button>
                            </Space>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                </div>
                <div className="d-flex justify-content-end">        
                <CustomPagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={knowledgeBase.length}
                  onPageChange={(page) => setPagination((prev) => ({ ...prev, current: page }))}
                  onPageSizeChange={(pageSize) => setPagination((prev) => ({ ...prev, pageSize, current: 1 }))}
                />
                </div>
              </>
            )}
          </>
        )}

        {/* Delete Confirmation Modal */}
        <Modal
          title="Delete"
          open={deleteModalVisible}
          onOk={handleDeleteModalOk}
          onCancel={handleDeleteModalCancel}
          okText="Yes, Delete"
          okButtonProps={{
            style: { backgroundColor: "red", borderColor: "red" },
          }}
          cancelText="Cancel"
          centered
        >
          <p>Are you sure you want to delete this knowledge base entry?</p>
        </Modal>

        {/* Knowledge Base Form Modal */}
        <Modal
          title="Add Knowledge Base Entry"
          open={showForm}
          onCancel={handleFormClose}
          footer={null}
          width={600}
          destroyOnClose={true}
        >
          <KnowledgeBaseForm
            onClose={handleFormClose}
            onSubmit={handleFormSubmit}
          />
        </Modal>
      
    </div>
  );
};

export default KnowledgeBaseList;
